import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MagnifyingGlassMinus, MagnifyingGlassPlus, MapPin, Trash, UploadSimple } from "@phosphor-icons/react";
import { api, lies, schick } from "../api/client";
import type { Mangel, Plan } from "../api/types";
import { Auswahl } from "./Auswahl";
import { Dialog, Meldung } from "./Bausteine";

/**
 * Lagepläne mit Mängel-Pins (SPEC §5.9). Ein Plan ist ein Bild (PDF wird beim Hochladen
 * gerechnet); Pins sitzen relativ (0..1) und bleiben beim Zoomen an ihrem Ort.
 */
export function usePlaene(projektId: string | undefined) {
  return useQuery({
    queryKey: ["plaene", projektId],
    queryFn: () => lies<Plan[]>(`/projekte/${projektId}/plaene`),
    enabled: !!projektId,
  });
}

const PIN: Record<string, string> = {
  offen: "pin-offen", angezeigt: "pin-offen", strittig: "pin-offen",
  beseitigt_gemeldet: "pin-gemeldet", erledigt: "pin-erledigt", entfallen: "pin-erledigt",
};

/** Ein Plan mit Pins. `klick` (relativ) macht ihn zur Ablagefläche fürs Verorten. */
function PlanBild({ plan, maengel, aktiv, klick, oeffnen }: {
  plan: Plan; maengel: Mangel[]; aktiv?: string; klick?: (x: number, y: number) => void; oeffnen?: (m: Mangel) => void;
}) {
  const [zoom, setZoom] = useState(1);
  const bild = useRef<HTMLImageElement>(null);
  return (
    <div className="plan">
      <div className="plan-zoom">
        <button className="knopf leise symbol" aria-label="Verkleinern" disabled={zoom <= 1} onClick={() => setZoom(Math.max(1, zoom / 1.5))}>
          <MagnifyingGlassMinus size={16} /></button>
        <button className="knopf leise symbol" aria-label="Vergrößern" disabled={zoom >= 6} onClick={() => setZoom(Math.min(6, zoom * 1.5))}>
          <MagnifyingGlassPlus size={16} /></button>
      </div>
      <div className="plan-flaeche">
        <div className="plan-inhalt" style={{ width: `${zoom * 100}%` }}>
          <img ref={bild} src={`/api/plaene/${plan.id}/bild`} alt={plan.titel} draggable={false}
               className={klick ? "plan-ziel" : ""}
               onClick={(e) => {
                 if (!klick || !bild.current) return;
                 const r = bild.current.getBoundingClientRect();
                 klick((e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
               }} />
          {maengel.filter((m) => m.plan_id === plan.id && m.plan_x != null && m.plan_y != null).map((m) => (
            <button key={m.id} className={`pin ${PIN[m.status] ?? ""}${aktiv === m.id ? " pin-aktiv" : ""}`}
                    style={{ left: `${m.plan_x! * 100}%`, top: `${m.plan_y! * 100}%` }}
                    title={`${m.nummer}: ${m.beschreibung}`} disabled={!oeffnen}
                    onClick={() => oeffnen?.(m)}>
              {m.lfd}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PlanWahl({ plaene, wert, setze }: { plaene: Plan[]; wert: string; setze: (v: string) => void }) {
  return (
    <Auswahl style={{ width: "auto", minWidth: 200 }} value={wert} onChange={(e) => setze(e.target.value)} aria-label="Plan">
      {plaene.map((p) => <option key={p.id} value={p.id}>{p.titel}{p.maengel ? ` (${p.maengel})` : ""}</option>)}
    </Auswahl>
  );
}

/** Ansicht im Mängel-Reiter: Plan wählen, hochladen, Pins antippen. */
export function PlanAnsicht({ projektId, maengel, kann, leitung, oeffnen }: {
  projektId: string; maengel: Mangel[]; kann: boolean; leitung: boolean; oeffnen: (m: Mangel) => void;
}) {
  const qc = useQueryClient();
  const plaene = usePlaene(projektId);
  const [gewaehlt, setGewaehlt] = useState("");
  const eingabe = useRef<HTMLInputElement>(null);
  const hoch = useMutation({
    mutationFn: (f: File) => {
      const d = new FormData();
      d.append("datei", f);
      return schick<Plan[]>(`/projekte/${projektId}/plaene`, d);
    },
    onSuccess: (liste) => { qc.setQueryData(["plaene", projektId], liste); setGewaehlt(liste.at(-1)?.id ?? ""); },
  });
  const weg = useMutation({
    mutationFn: (id: string) => api<Plan[]>("DELETE", `/plaene/${id}`),
    onSuccess: (liste) => { qc.setQueryData(["plaene", projektId], liste); setGewaehlt(""); qc.invalidateQueries({ queryKey: ["maengel"] }); },
  });
  const liste = plaene.data ?? [];
  const plan = liste.find((p) => p.id === gewaehlt) ?? liste[0];
  const ohne = maengel.filter((m) => !m.plan_id).length;
  return (
    <div>
      <div className="punkt-leiste" style={{ marginBottom: 10 }}>
        {liste.length > 0 && plan && <PlanWahl plaene={liste} wert={plan.id} setze={setGewaehlt} />}
        {kann && <>
          <button className="knopf" disabled={hoch.isPending} onClick={() => eingabe.current?.click()}>
            <UploadSimple size={16} /> {hoch.isPending ? "rechnet …" : "Plan hochladen"}</button>
          <input ref={eingabe} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                 onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) hoch.mutate(f); }} />
        </>}
        {plan && leitung && (
          <button className="knopf leise symbol gefahr" aria-label="Plan entfernen"
                  onClick={() => confirm(`Plan „${plan.titel}“ entfernen? Die Mängel bleiben, nur ihre Verortung entfällt.`) && weg.mutate(plan.id)}>
            <Trash size={16} /></button>
        )}
        <div style={{ flex: 1 }} />
        {ohne > 0 && <span className="mute">{ohne} Mängel noch nicht verortet – im Mangel „Im Plan verorten“</span>}
      </div>
      <Meldung fehler={hoch.error || weg.error || plaene.error} />
      {plan ? <PlanBild plan={plan} maengel={maengel} oeffnen={oeffnen} />
            : plaene.isSuccess && <div className="leer">Noch kein Lageplan. PDF (erste Seite), PNG oder JPEG hochladen.</div>}
    </div>
  );
}

/** Dialog im Mangel: Plan wählen, Stelle antippen, speichern. */
export function Verorten({ mangel, zu }: { mangel: Mangel; zu: () => void }) {
  const qc = useQueryClient();
  const plaene = usePlaene(mangel.projekt_id);
  const [planId, setPlanId] = useState(mangel.plan_id ?? "");
  const [ort, setOrt] = useState<[number, number] | null>(mangel.plan_x != null ? [mangel.plan_x, mangel.plan_y!] : null);
  const liste = plaene.data ?? [];
  const plan = liste.find((p) => p.id === planId) ?? liste[0];
  const speichern = useMutation({
    mutationFn: (r: { plan_id: string | null; x?: number; y?: number }) => api("PUT", `/maengel/${mangel.id}/verortung`, r),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["maengel"] }); qc.invalidateQueries({ queryKey: ["mangel", mangel.id] });
                       qc.invalidateQueries({ queryKey: ["plaene"] }); zu(); },
  });
  const vorschau: Mangel = { ...mangel, plan_id: plan?.id ?? null, plan_x: ort?.[0] ?? null, plan_y: ort?.[1] ?? null };
  return (
    <Dialog offen zu={zu} titel={`${mangel.nummer} im Plan verorten`} fuss={<>
      {mangel.plan_id && <button className="knopf leise gefahr" style={{ marginRight: "auto" }}
                                 onClick={() => speichern.mutate({ plan_id: null })}>Verortung entfernen</button>}
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={!plan || !ort || speichern.isPending}
              onClick={() => plan && ort && speichern.mutate({ plan_id: plan.id, x: ort[0], y: ort[1] })}>
        <MapPin size={16} /> Speichern</button>
    </>}>
      {liste.length === 0 && plaene.isSuccess && <p>Es gibt noch keinen Lageplan. Im Reiter „Mängel“ unter „Lageplan“ hochladen.</p>}
      {plan && <>
        {liste.length > 1 && <div style={{ marginBottom: 8 }}>
          <PlanWahl plaene={liste} wert={plan.id} setze={(v) => { setPlanId(v); setOrt(null); }} /></div>}
        <p className="mute" style={{ marginTop: 0 }}>Auf die Stelle des Mangels tippen.</p>
        <PlanBild plan={plan} maengel={[vorschau]} aktiv={mangel.id} klick={(x, y) => setOrt([x, y])} />
      </>}
      <Meldung fehler={speichern.error} />
    </Dialog>
  );
}
